import React, { useState, useEffect, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { getSymbols } from '../../utils/api';
import { usePrice } from '../../context/PriceContext';
import styles from './OptionChainPage.module.css';
import { FaChartLine, FaChevronDown } from 'react-icons/fa';

const STRIKE_COUNT_AROUND_ATM = 10; 

const getStrikeIncrement = (underlying) => {
    const up = underlying.toUpperCase();
    if (up.includes('NIFTY 50') || up === 'NIFTY') return 50;
    return 100;
};

// Helper to parse the F&O symbol string (from old_frontend)
const parseFnoSymbol = (symbol) => {
    if (typeof symbol !== 'string') return null;
    const parts = symbol.split(' ');
    if (parts.length < 5) return null; 

    const underlying = parts[0];
    const strike = parseFloat(parts[1]);
    const type = parts[2];
    const expiry = parts.slice(3).join(' ');

    if (type !== 'CE' && type !== 'PE') return null;
    if (!underlying || isNaN(strike) || !expiry) return null;

    return { underlying, strike, type, expiry, full: symbol };
};

/**
 * Helper to format expiry date for display
 */
const formatExpiryDisplay = (expiry) => {
    return expiry; // In current system, expiry is already formatted like "30 MAR 2026"
};

/**
 * Custom Professional Dropdown for Expiry - Uses Portal and auto-closes on scroll
 */
const ExpiryDropdown = ({ options, selected, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

    const updateCoords = () => {
        if (dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            setCoords({
                top: rect.bottom,
                left: rect.left,
                width: rect.width
            });
        }
    };

    useEffect(() => {
        if (isOpen) {
            updateCoords();
            window.addEventListener('resize', updateCoords);
            window.addEventListener('scroll', updateCoords, true);
            
            const header = dropdownRef.current?.closest(`.${styles.indexHeader}`);
            const handleScroll = () => setIsOpen(false);
            if (header) header.addEventListener('scroll', handleScroll);
            
            return () => {
                window.removeEventListener('resize', updateCoords);
                window.removeEventListener('scroll', updateCoords, true);
                if (header) header.removeEventListener('scroll', handleScroll);
            };
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (val) => {
        onChange(val);
        setIsOpen(false);
    };

    return (
        <div className={styles.expiryDropdown} ref={dropdownRef} onClick={() => setIsOpen(!isOpen)}>
            <div className={styles.dropdownValue}>
                {formatExpiryDisplay(selected)}
            </div>
            <div className={styles.dropdownIconBox}>
                <FaChevronDown />
            </div>
            {isOpen && createPortal(
                <div 
                    className={styles.dropdownMenu} 
                    style={{ 
                        top: `${coords.top + 4}px`, 
                        left: `${coords.left}px`,
                        width: `${coords.width}px`
                    }}
                >
                    {options.map(opt => (
                        <div 
                            key={opt} 
                            className={`${styles.dropdownItem} ${opt === selected ? styles.dropdownItemActive : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleSelect(opt); }}
                        >
                            {formatExpiryDisplay(opt)}
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

const OptionRow = memo(({ symbol, direction, isCall }) => {
    const navigate = useNavigate();
    const [showOptions, setShowOptions] = useState(false);
    const { price: ltp, lastDayClose } = usePrice(symbol);

    useEffect(() => {
        let timer;
        if (showOptions) {
            timer = setTimeout(() => { setShowOptions(false); }, 3000);
        }
        return () => { clearTimeout(timer); };
    }, [showOptions]);

    if (!symbol) return <div className={`${styles.optionCell} ${styles.disabled}`}>-</div>;

    const openOrder = (tradeType) => {
        navigate(`/order/${symbol}/${tradeType}`, { 
            state: { livePrice: ltp } 
        });
    };

    const openChart = () => navigate('/trade', { state: { chartSymbol: symbol } });
    
    let colorClass = '';
    const priceChange = ltp != null && lastDayClose != null ? ltp - lastDayClose : 0;
    if (priceChange > 0) colorClass = styles.positive;
    else if (priceChange < 0) colorClass = styles.negative;

    const priceChangePercent = ltp != null && lastDayClose != null && lastDayClose !== 0 
        ? (priceChange / lastDayClose) * 100 : 0;

    // Show percentage change in place of volume
    const percentContent = (
        <span className={`${styles.volume} ${isCall ? styles.callVolume : styles.putVolume} ${colorClass}`}>
            {ltp != null ? `${priceChangePercent.toFixed(2)}%` : '--'}
        </span>
    );

    // Show price and price change
    const priceContent = (
        <div className={styles.priceContainer}>
            <span className={styles.price}>{ltp != null ? ltp.toFixed(2) : '--'}</span>
            <span className={`${styles.percent} ${colorClass}`}>
                {ltp != null ? (priceChange >= 0 ? `+${priceChange.toFixed(2)}` : priceChange.toFixed(2)) : '--'}
            </span>
        </div>
    );

    return (
        <div className={styles.optionCell} onClick={() => setShowOptions(true)}>
            <div className={styles.optionData}>
                {direction === 'left' ? (
                    <>
                        {percentContent}
                        {priceContent}
                    </>
                ) : (
                    <>
                        {priceContent}
                        {percentContent}
                    </>
                )}
            </div>
            {showOptions && (
                <div className={styles.actions}>
                    <button className={styles.buy} onClick={(e) => { e.stopPropagation(); openOrder('buy'); }}>B</button>
                    <button className={styles.sell} onClick={(e) => { e.stopPropagation(); openOrder('sell'); }}>S</button>
                    <button className={styles.chart} onClick={(e) => { e.stopPropagation(); openChart(); }}><FaChartLine/></button>
                </div>
            )}
        </div>
    );
});

const INDICES = ['NIFTY', 'BANKNIFTY', 'SENSEX', 'BANKEX'];
const DISPLAY_NAMES = {
    'NIFTY': 'Nifty 50',
    'BANKNIFTY': 'Nifty Bank',
    'SENSEX': 'SENSEX',
    'BANKEX': 'BANKEX'
};

const OptionChainPage = () => {
    const { symbol: initialSymbol } = useParams();
    const navigate = useNavigate();

    const normalizeName = (s) => {
        const up = s?.toUpperCase() || '';
        if (up === 'NIFTY' || up === 'NIFTY 50') return 'NIFTY';
        if (up === 'BANKNIFTY' || up === 'NIFTY BANK') return 'BANKNIFTY';
        return up;
    };

    const [underlying, setUnderlying] = useState(normalizeName(initialSymbol));
    const [allSymbols, setAllSymbols] = useState([]);
    const [expiryDates, setExpiryDates] = useState([]);
    const [selectedExpiry, setSelectedExpiry] = useState('');
    const [chainRows, setChainRows] = useState([]); 
    const [atmStrike, setAtmStrike] = useState(0);
    
    const atmRowRef = useRef(null);
    const chainBodyRef = useRef(null);
    const lastUserScrollTimeRef = useRef(Date.now());
    const isAutoScrollingRef = useRef(false);

    const { price: liveIndexPrice, lastDayClose: indexLastDayClose } = usePrice(underlying);

    const scrollToAtm = (behavior = 'smooth') => {
        if (atmRowRef.current && chainBodyRef.current) {
            isAutoScrollingRef.current = true;
            atmRowRef.current.scrollIntoView({ behavior, block: 'center' });
            lastUserScrollTimeRef.current = Date.now();
            setTimeout(() => { isAutoScrollingRef.current = false; }, 1000);
        }
    };

    useEffect(() => {
        const normalized = normalizeName(initialSymbol);
        if (normalized !== underlying) {
            setUnderlying(normalized);
            setChainRows([]);
            setAtmStrike(0);
        }
    }, [initialSymbol, underlying]);

    // Fetch symbols on mount
    useEffect(() => {
        const fetchAllSymbols = async () => {
            try {
                const { data } = await getSymbols();
                const fnoSymbols = data.filter(s => s && s.symbol_type === 'F&O');
                setAllSymbols(fnoSymbols);
            } catch (error) {
                console.error("Error fetching symbols:", error);
            }
        };
        fetchAllSymbols();
    }, []);

    // Extract expiries for selected underlying
    useEffect(() => {
        if (!underlying || allSymbols.length === 0) return;

        const expiries = allSymbols
            .map(s => parseFnoSymbol(s.user_symbol))
            .filter(parsed => parsed && parsed.underlying === underlying)
            .map(parsed => parsed.expiry);

        const uniqueExpiries = [...new Set(expiries)];
        setExpiryDates(uniqueExpiries);

        if (uniqueExpiries.length > 0 && (!selectedExpiry || !uniqueExpiries.includes(selectedExpiry))) {
            setSelectedExpiry(uniqueExpiries[0]);
        }
    }, [underlying, allSymbols, selectedExpiry]);

    // Center when atmStrike changes or chainRows is first built
    useEffect(() => {
        if (chainRows.length > 0 && atmStrike) {
            const timer = setTimeout(() => scrollToAtm('smooth'), 100);
            return () => clearTimeout(timer);
        }
    }, [atmStrike, chainRows.length]);

    // Track user scroll and auto-center
    useEffect(() => {
        const handleUserScroll = () => {
            if (!isAutoScrollingRef.current) {
                lastUserScrollTimeRef.current = Date.now();
            }
        };
        const body = chainBodyRef.current;
        if (body) body.addEventListener('scroll', handleUserScroll, { passive: true });
        const interval = setInterval(() => {
            if (Date.now() - lastUserScrollTimeRef.current >= 10000) scrollToAtm('smooth');
        }, 2000);
        return () => {
            if (body) body.removeEventListener('scroll', handleUserScroll);
            clearInterval(interval);
        };
    }, []);

    // Build Chain
    useEffect(() => {
        if (!liveIndexPrice || !selectedExpiry || allSymbols.length === 0) return;

        const increment = getStrikeIncrement(underlying);
        const calculatedAtmStrike = Math.round(liveIndexPrice / increment) * increment;
        setAtmStrike(calculatedAtmStrike);

        const strikesToShow = Array.from(
            { length: STRIKE_COUNT_AROUND_ATM * 2 + 1 },
            (_, i) => calculatedAtmStrike + (i - STRIKE_COUNT_AROUND_ATM) * increment
        ).filter(strike => strike > 0);
        
        const optionsForExpiry = allSymbols.filter(s => {
            const parsed = parseFnoSymbol(s.user_symbol);
            return parsed && parsed.underlying === underlying && parsed.expiry === selectedExpiry;
        });

        const newRows = strikesToShow.map(strike => {
            const call = optionsForExpiry.find(s => {
                const parsed = parseFnoSymbol(s.user_symbol);
                return parsed && parsed.strike === strike && parsed.type === 'CE';
            });
            const put = optionsForExpiry.find(s => {
                const parsed = parseFnoSymbol(s.user_symbol);
                return parsed && parsed.strike === strike && parsed.type === 'PE';
            });
            return { strike, callSymbol: call?.user_symbol, putSymbol: put?.user_symbol };
        });

        setChainRows(newRows);
    }, [liveIndexPrice, selectedExpiry, underlying, allSymbols]);

    const selectUnderlying = (val) => {
        if (val === underlying) return;
        setUnderlying(val);
        setSelectedExpiry('');
        setChainRows([]);
        setAtmStrike(0);
        navigate(`/option-chain/${val}`, { replace: true });
    };

    const handleExpiryChange = (val) => {
        setSelectedExpiry(val);
    };

    return (
        <div className={styles.container}>
            <div className={styles.indexHeader}>
                {INDICES.map(idx => {
                    const isActive = idx === underlying;
                    return (
                        <IndexCard 
                            key={idx} 
                            symbol={idx} 
                            isActive={isActive} 
                            onSelect={selectUnderlying}
                            expiryDates={expiryDates}
                            selectedExpiry={selectedExpiry}
                            onExpiryChange={handleExpiryChange}
                        />
                    );
                })}
            </div>
            <div className={styles.chainTable}>
                <div className={styles.chainHeader}>
                    <div className={styles.chainSideHeader}><span>CALL %CHNG</span><span>CALL LTP</span></div>
                    <div className={styles.strikeHeader}>STRIKE</div>
                    <div className={styles.chainSideHeader}><span>PUT LTP</span><span>PUT %CHNG</span></div>
                </div>
                <div className={styles.chainBody} ref={chainBodyRef}>
                    {chainRows.length === 0 && <div className={styles.loading}>Building chain...</div>}
                    {chainRows.map((row) => (
                        <div 
                            key={`${row.strike}-${selectedExpiry}`} 
                            ref={row.strike === atmStrike ? atmRowRef : null}
                            className={`${styles.chainRow} ${row.strike === atmStrike ? styles['atm-row-highlight'] : ''}`}
                        >
                            <OptionRow 
                                symbol={row.callSymbol} 
                                direction="left" 
                                isCall={true}
                            />
                            <div className={styles.strikePrice}>{row.strike}</div>
                            <OptionRow 
                                symbol={row.putSymbol} 
                                direction="right" 
                                isCall={false}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const IndexCard = ({ symbol, isActive, onSelect, expiryDates, selectedExpiry, onExpiryChange }) => {
    const { price, lastDayClose } = usePrice(symbol);
    const change = price && lastDayClose ? price - lastDayClose : 0;
    const changePct = lastDayClose ? (change / lastDayClose) * 100 : 0;
    const colorClass = change > 0 ? styles.positive : change < 0 ? styles.negative : '';

    return (
        <div 
            className={`${styles.indexCard} ${isActive ? styles.activeCard : ''}`}
            onClick={() => onSelect(symbol)}
        >
            <div className={styles.cardLeft}>
                <span className={styles.indexName}>{DISPLAY_NAMES[symbol]}</span>
                {isActive ? (
                    <ExpiryDropdown 
                        options={expiryDates}
                        selected={selectedExpiry}
                        onChange={onExpiryChange}
                    />
                ) : (
                    expiryDates[0] && <span className={styles.expiryStatic}>{formatExpiryDisplay(expiryDates[0])}</span>
                )}
            </div>
            <div className={styles.cardRight}>
                <span className={styles.indexPrice}>{price ? price.toFixed(2) : '--'}</span>
                <span className={`${styles.indexChange} ${colorClass}`}>
                    {price ? `${change > 0 ? '+' : ''}${change.toFixed(2)} (${changePct.toFixed(2)}%)` : '--'}
                </span>
            </div>
        </div>
    );
};

export default OptionChainPage;
